/* eslint-disable linebreak-style */
import React, { useEffect } from 'react';
import { VStack, Stack, Text, Flex, Button, HStack, Box, useDisclosure, Progress, useMediaQuery } from '@chakra-ui/react';
import { KycVerifiedIcon, KycUnverifiedIcon, KycDeniedIcon, RequestSuccessfulIcon } from 'Components/Icons';
import { useGetKycStatusQuery } from 'services';
import { useAppSelector, useTilia } from 'Hooks';
import { CustomModal } from '../../Rewards/Components/CustomModal';
import { useDefaultErrorHandler } from 'Utils/errorHandling';
import { useLocale } from 'context/LocalizationContext';

interface IRequestSuccessProps {
	onClose: () => void;
}

export const RequestSuccess = ({ onClose }: IRequestSuccessProps) => {
	return (
		<VStack py={'50px'} spacing={3} align={'center'} justifyContent={'center'}>
			<RequestSuccessfulIcon w={['46px', '56px']} h={['46px', '56px']} />
			<Text fontSize={['16px', '22px']} fontWeight={600}>
				Request Successful
			</Text>
			<Text fontSize={['12px', '14px']} textAlign={'center'} pb={'50px'} w={'400px'}>
				Your request is successfully done, your profile will be updated as soon as it is completed.
			</Text>
			<Button variant={'primary'} w={['280px', '400px']} h={'40px'} onClick={onClose}>
				Done
			</Button>
		</VStack>
	);
};

export const KycStatus = () => {
	const { locale } = useLocale();
	const flow = 'kyc';
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { userInfo } = useAppSelector((state) => state.authUser);
	const { data: kycStatus, isError: isKycError, error: kycError } = useGetKycStatusQuery({ locale });
	const [isLargerThan768] = useMediaQuery('(min-width: 768px)');
	const isVerificationDisabled = !kycStatus?.data || kycStatus?.data.isValid || kycStatus.data?.isProcessing;
	const {
		loadWidgetScript,
		isLoadingRedirect,
		initTiliaWidget,
		onCompleteCallbackPayload,
		isError: isWidgetError,
		error: widgetError,
	} = useTilia({ flow });

	// Display KYC errors only if they are meaningful and not caused by the absence of a Tilia account.
	// Note: A Tilia account is created upon email confirmation.
	const shouldHandleKycError = Boolean(userInfo && userInfo?.emailConfirmed && isKycError);

	useDefaultErrorHandler(shouldHandleKycError, kycError);
	useDefaultErrorHandler(isWidgetError, widgetError, onClose);

	useEffect(() => {
		loadWidgetScript();
	}, []);

	const handleClick = () => {
		onOpen();
		initTiliaWidget();
	};

	return (
		<Stack spacing={3}>
			<Flex alignItems='center' justifyContent='space-between'>
				<Text fontWeight='semibold'>KYC Verification</Text>
				<Button
					color={kycStatus?.data?.isValid ? 'loopGrey.700' : 'loop.500'}
					disabled={isVerificationDisabled}
					_hover={
						isVerificationDisabled
							? {}
							: { textDecoration: 'underline',}
					}
					onClick={handleClick}
				>
					{kycStatus?.data?.canResubmit ? 'Reverify' : 'Verify'}
				</Button>
			</Flex>
			{kycStatus && (
				<HStack spacing={1}>
					{!kycStatus.data ? (
						<>
							<KycDeniedIcon />
							<Text fontWeight='semibold' color='kyc.red'>
								Failed to retrieve KYC status
							</Text>
						</>
					) : kycStatus.data?.isValid ? (
						<>
							<KycVerifiedIcon />
							<Text fontWeight='semibold' color='kyc.green'>
								Verified
							</Text>
						</>
					) : kycStatus.data?.isNoData ? (
						<>
							<KycUnverifiedIcon />
							<Text fontWeight='semibold' color='kyc.yellow'>
								KYC Not Completed
							</Text>
						</>
					) : kycStatus.data?.isProcessing ? (
						<>
							<KycUnverifiedIcon />
							<Text fontWeight='semibold' color='kyc.yellow'>
								KYC Check Processing
							</Text>
						</>
					) : (
						<>
							<KycDeniedIcon />
							<Text fontWeight='semibold' color='kyc.red'>
								Denied
							</Text>
						</>
					)}
				</HStack>
			)}
			<CustomModal
				content={
					<>
						{isLoadingRedirect && (
							<Progress
								size='xs'
								isIndeterminate
								position='fixed'
								top={0}
								left={0}
								right={0}
								zIndex='10000'
								colorScheme='red'
							/>
						)}
						{onCompleteCallbackPayload ? <RequestSuccess onClose={onClose} /> : <Box id='tilia-widget-wrapper' />}
					</>
				}
				size={isLargerThan768 ? 'md' : 'full'}
				isOpen={isOpen}
				onClose={onClose}
				modalContentStyles={{ borderRadius: isLargerThan768 ? 10 : 0, h: isLargerThan768 ? '635px' : '100%' }}
			/>
		</Stack>
	);
};
